<script>
import axios from "axios";
import { mapGetters } from "vuex";
import ApplicationProgressTracker from "@/components/base/ApplicationProgressTracker.vue";
import UserBioCard from "@/components/base/UserBioCard.vue";
import EmailCard from "@/components/EmailCard.vue";
import ScheduleCard from "@/components/ScheduleCard.vue";
import TodoCard from "@/components/TodoCard.vue";
import MyCoursesCard from "@/components/MyCoursesCard.vue";

export default {
  name: "AppDashboard",
  components: {
    ApplicationProgressTracker,
    UserBioCard,
    EmailCard,
    ScheduleCard,
    TodoCard,
    MyCoursesCard,
  },
  data: () => ({
    systemEnabled: false,
    progress: null,
    loading: true,
    error: "",
  }),
  computed: {
    ...mapGetters(["getUser", "getApplication", "getStudent"]),
    displayNumber() {
      return (
        this.getStudent?.matriculationNumber ||
        this.getApplication?.applicationNumber ||
        ""
      );
    },
    isEnrolled() {
      return Boolean(
        this.getStudent?.matriculationNumber &&
          this.getStudent?.status === "active"
      );
    },
    trackerStages() {
      return this.progress?.trackerStages || [];
    },
    todos() {
      const action = this.progress?.nextAction;
      if (!action || action.code === "application_complete") return [];
      return [{ ...action, type: "application" }];
    },
    quickLinks() {
      return [
        { label: "Print ID Card", icon: "bi-person-badge", route: "resources" },
        {
          label: "Campus Map",
          icon: "bi-map-fill",
          route: "resources",
          hash: "#map",
        },
        {
          label: "Complaints",
          icon: "bi-chat-text",
          route: "resources",
          hash: "#contacts",
        },
        { label: "Payments", icon: "bi-cash-coin", route: "finance" },
        {
          label: "Appointments",
          icon: "bi-calendar-check",
          route: "resources",
        },
        { label: "TimeTable", icon: "bi-table", route: "academics" },
        { label: "Grades", icon: "bi-award", route: "academics" },
        {
          label: "Transcript",
          icon: "bi-file-earmark-text",
          route: "resources",
        },
        { label: "GP Projection", icon: "bi-bar-chart", route: "academics" },
        {
          label: "Scholarships",
          icon: "bi-mortarboard",
          route: "finance",
          hash: "#scholarships",
        },
      ].map((link) => ({
        ...link,
        to: { name: link.route, ...(link.hash ? { hash: link.hash } : {}) },
      }));
    },
  },
  async mounted() {
    await this.refresh();
  },
  methods: {
    async refresh() {
      this.loading = true;
      this.error = "";
      try {
        await this.$store.dispatch("refreshSession");
        const response = await axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/applications/me`
        );
        this.progress = response.data?.data?.progress || null;
      } catch (error) {
        this.error =
          error.response?.data?.message || "Unable to refresh the dashboard.";
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<template>
  <div class="container-fluid p-4 text-start">
    <div class="d-flex justify-content-between align-items-center">
      <div>
        <h4 class="fw-bold mb-1">
          Welcome,
          <span class="text-capitalize">{{ getUser?.firstName }}</span> 👋🏼
        </h4>
        <!-- <span class="text-muted">{{ displayNumber }}</span> -->
        <span class="text-muted"
          >Here's what's happening with your studies today.</span
        >
      </div>
      <button
        class="btn btn-outline-primary btn-sm"
        :disabled="loading"
        title="Refresh dashboard data"
        @click="refresh"
      >
        <i class="bi bi-arrow-clockwise" :class="{ spin: loading }"></i>
        {{ loading ? "Refreshing..." : "Refresh" }}
      </button>
    </div>
    <hr />

    <div v-if="error" class="alert alert-danger">{{ error }}</div>

    <div v-if="!isEnrolled" class="row mb-5">
      <div class="col-md-12">
        <ApplicationProgressTracker
          :stages="trackerStages"
          :loading="loading"
        />
      </div>
    </div>

    <div class="row g-4 mb-5">
      <div class="col-md-6 col-lg-4 mb-5 mb-md-0">
        <h6>Biodata</h6>
        <UserBioCard class="h-100" />
      </div>

      <div v-if="!isEnrolled" class="col-md-6 col-lg-8">
        <h6>
          Todo
          <span v-if="todos.length" class="badge text-bg-danger">
            {{ todos.length }}
          </span>
        </h6>
        <TodoCard :todos="todos" :loading="loading" class="h-100" />
      </div>

      <div v-if="isEnrolled" class="col-md-6 col-lg-3">
        <h6>E-mail</h6>
        <EmailCard :enabled="false" style="min-height: 400px" />
      </div>

      <div v-if="isEnrolled" class="col-md-12 col-lg-5">
        <h6>Schedule</h6>
        <ScheduleCard :enabled="false" style="min-height: 400px" />
      </div>
    </div>

    <div v-if="isEnrolled" class="row mb-5">
      <div class="col-md-6">
        <h6>Upcoming Events</h6>
        <div
          class="card border-0 shadow-sm overflow-scroll position-relative"
          style="max-height: 550px"
        >
          <div v-for="n in 2" :key="n">
            <div
              class="bg-secondary"
              style="min-height: 175px; width: 100%"
            ></div>
            <div class="card-body pb-0">
              <p class="placeholder-glow mb-2">
                <span class="placeholder col-6"></span>
              </p>
              <p class="placeholder-glow small">
                <span class="placeholder col-3 me-2"></span>
                <span class="placeholder col-7"></span>
              </p>
            </div>
          </div>
          <div
            v-if="!systemEnabled"
            class="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-dark bg-opacity-50 rounded-3"
            style="z-index: 10; cursor: not-allowed"
          >
            <h5 class="text-white fw-bold">Not activated yet</h5>
          </div>
        </div>
      </div>

      <div class="col-md-6 mt-md-0 mt-4">
        <h6>Quick Links</h6>
        <div
          class="card border-0 shadow-sm p-3 overflow-auto"
          style="height: 230px"
        >
          <div class="d-flex flex-wrap gap-3 justify-content-evenly">
            <RouterLink
              v-for="link in quickLinks"
              :key="link.label"
              :to="link.to"
              class="btn btn-dark d-flex flex-column align-items-center justify-content-center p-3 quick-link"
            >
              <i :class="`bi ${link.icon} fs-2`"></i>
              <small>{{ link.label }}</small>
            </RouterLink>
          </div>
        </div>

        <h6 class="mt-4">My Courses</h6>
        <MyCoursesCard height="250px" />
      </div>
    </div>
  </div>
</template>

<style scoped>
.spin {
  display: inline-block;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
